/**
 * The realm eats.
 *
 * The owner's complaint, verbatim: *"i think you need to make food actually
 * matter, and tick down a month based on pop i dont think its doing anything"*.
 * They were right, and the market measured it: a third of the order book was
 * grain asks that never filled, because grain was a good every crown produced,
 * no crown ever spent, and therefore nobody would buy at any price.
 *
 * ── The rule, in one sentence ──────────────────────────────────────────────
 *
 * **Every month, a crown pays a bill in grain scaled by the people it holds and
 * the hosts it feeds — out of its city yards first and its national stores
 * after — and whatever it cannot pay comes back as unrest.**
 *
 * That is the whole mechanic. Everything below is the argument for each half of
 * it, because every half had a plausible alternative that would have been worse.
 *
 * ── What counts as "pop" ───────────────────────────────────────────────────
 *
 * The campaign layer has no population counter and should not grow one: the
 * settlement has villagers, the realm has *ground*. So the bill is read off the
 * two things the map already tracks and the player already looks at — **the
 * provinces a crown holds and how far up the ladder each one has been built**,
 * plus **the points its hosts have in the field**. A tier-8 city eats four times
 * what a hamlet eats, and a host on the march eats whether or not it is fighting.
 *
 * A per-province flat charge alone was rejected because it makes the ladder
 * free: a crown that builds a great city would feed it out of the same bill as
 * the steading it grew from, and the tier ladder is the one place on the map
 * where "bigger town" is a thing the player deliberately builds. Levy points
 * were rejected as the host term because manpower banked at home is not a
 * standing army being fed; `rawPoints` is what is actually standing on the
 * board, and it is already what `UPKEEP_GOLD_PER_POINT` charges wages against —
 * so the food bill and the wage bill read the same host, which is the sort of
 * agreement that stops two numbers drifting.
 *
 * The bill is deliberately **not** scaled by unrest, development or a siege. A
 * restive province eats. A blockaded one eats. Yield is what a province *gives*
 * the crown and it is right that it should collapse under a siege; hunger is
 * what the people *need*, and a rule where a besieged city costs less to feed
 * would be a rule that rewards being besieged.
 *
 * ── Which purse it comes out of, and why both ──────────────────────────────
 *
 * The strategic layer already has two places a good can be, and the split is
 * load-bearing rather than incidental (`holdings.ts`): **materials are carted to
 * city yards, coin and souls stay national.** Grain is a material, so the tithe
 * lands in the yards — and the market, which is where the owner's complaint came
 * from, trades out of the *national* treasury. A bill that read only one of them
 * would have been a lie about the other: charge the yards alone and a crown
 * could buy a thousand grain on the book and starve holding it; charge the
 * treasury alone and the tithe would feed nobody.
 *
 * So the bill draws **the yards first, in province order, then the national
 * stores**. One rule, and it makes both flows converge on the same mouth: the
 * country feeds itself out of its own granaries, and what the crown buys abroad
 * is the reserve underneath them. It is also the right fiction — a realm eats
 * the harvest in the barn before it opens the strategic reserve.
 *
 * ── What famine does, and what it deliberately does not ────────────────────
 *
 * It raises **unrest**, and nothing else, and it does that through the field
 * every other system on this layer already reads. That single write reaches:
 * income and the tithe (`yieldScale` multiplies by `1 - unrest`, so a hungry
 * realm grows less next month — the feedback loop the mechanic needs), the levy
 * (same term in `holdingIncome`), the Temple and the Great Families (both
 * `estateTarget`s read mean unrest), and through the Families' standing, the
 * garrison regrowth. Four consequences, one door, no new meter.
 *
 * There is **no death mechanic and there must not be one.** A starving crown
 * gets poorer, weaker and more restive; it does not lose provinces to hunger and
 * cannot be eliminated by it. Verified by the sweep — see `COORDINATION.md`.
 *
 * The Great Families get one term of their own, `familiesTarget`'s larder line,
 * and it reads the *stores* rather than this month's shortfall: "the granaries
 * are empty" is a standing complaint about a crown's husbandry, not an event.
 * That keeps it derivable from the board like every other estate term, which is
 * the property that lets `estateReport` explain itself.
 *
 * ── No new state ───────────────────────────────────────────────────────────
 *
 * Every number here is a pure function of provinces, tiers, armies, city yards
 * and treasuries — all of which are already hashed and saved. The bill is
 * therefore not in `hashCampaign` or `saveCampaign` **because it is not state**,
 * and a save written before this file loads into a realm that has always eaten.
 * Nothing here draws a random number.
 */

import { RESOURCE_LABELS } from '../types.ts';
import { clamp } from '../../core/math.ts';
import { rawPoints } from './armies.ts';
import { citiesOf, holdingIncome, tierOf } from './holdings.ts';
import { logLine } from './state.ts';
import type { CampaignState } from './types.ts';

/**
 * Grain a month for holding a province at all, at the bottom rung.
 *
 * Measured, not guessed, and the three constants below were tuned together
 * against a 4-seed × 120-turn probe rather than argued from first principles.
 *
 * A first-rung province tithes `TITHE_BASE = 0.5` a month of its local good,
 * and three of the ten terrains (plain, marsh, coast) tithe grain — and terrain
 * is clustered by region, so grain is not spread evenly over the board but
 * pooled in some neighbourhoods and absent from others. Measured across the
 * realm: a crown holds 8.9 provinces and grows 8.8 grain a month against a bill
 * of 5.5, **half of all crown-months feed themselves outright, a fifth grow
 * under half of what they eat, and a sixth grow essentially none.**
 *
 * That spread is the point and it is why the numbers were left where they are.
 * Food is a reason to hold the right ground, a reason to trade with whoever
 * holds it, and — for the crowns in the stone regions — the reason there is a
 * buyer on the other side of every grain ask. A bill low enough to feed the
 * mountain crowns out of their own dirt would be a bill nobody ever noticed.
 */
export const FOOD_PER_HOLDING = 0.34;

/**
 * Extra grain a month per rung above the first.
 *
 * A tier-10 great city eats 0.34 + 9 × 0.22 = 2.3 a month, near seven times a
 * hamlet, against a tithe that has risen from 0.5 to 8.6 over the same ladder.
 * The ladder still pays for itself in food several times over — it must, or
 * nobody would ever climb it — but a tall realm carries a visibly heavier bill
 * than a wide one of the same province count, and a great city cut off from the
 * fields that feed it is now a thing that can happen to somebody.
 *
 * It is a small term realm-wide, and that is honest rather than a failure:
 * the board averages 2.4 rungs above the first per crown, because the AI barely
 * builds. It is the player's term.
 */
export const FOOD_PER_TIER = 0.22;

/**
 * Grain a month per point of host standing on the board.
 *
 * A crown fields about 2,180 points across all its hosts in a typical month, so
 * this is ~2.0 grain — a third of the average bill, against the ground's two
 * thirds. That ratio was tuned rather than picked: at the first draft's 0.002 a
 * point the hosts were 56% of every bill and the mechanic read as a second wage
 * bill wearing a hat, which is not what the owner asked for. Two thirds of the
 * bill is *people*, which is the brief.
 *
 * Set against `UPKEEP_GOLD_PER_POINT`, which charges the same host in coin: the
 * two bills read the same `rawPoints`, so a crown that raises everything it can
 * afford in gold now also notices it in bread. This is the term that makes a
 * war-swollen crown go short while its neighbour at peace does not.
 */
export const FOOD_PER_POINT = 0.0009;

/**
 * Unrest a month across every province of a crown that cannot pay its bill in
 * full, at total famine; scaled linearly by how much of the bill went unpaid.
 *
 * `UNREST_DECAY` is 0.09 a month, so a crown missing half its bill is roughly
 * holding station and a crown paying none of it climbs about 0.03 a month —
 * a year and a half from a settled realm to the ceiling below, which is slow
 * enough to be a warning and fast enough to be a problem. Realm-wide the
 * mechanic moves mean unrest from 0.066 to 0.136; anything sharper took it past
 * 0.28 and made every crown on the board poor, which is a rebalance of the
 * whole game rather than a food system.
 */
export const FAMINE_UNREST = 0.12;

/**
 * How restive hunger alone can make a country.
 *
 * The one number in this file that exists to stop a feedback loop, and it is
 * not optional. Unrest cuts yield, yield includes the grain tithe, and the bill
 * is deliberately *not* scaled by unrest — so without a ceiling a land-poor
 * crown that misses one harvest climbs to unrest 1.0, earns nothing ever again,
 * and sits there for the rest of the campaign. Measured: a third of the realm
 * pinned in exactly that state by turn 60. That is the mass-starvation failure
 * the brief forbids, arriving as a permanent coma rather than as a death.
 *
 * At 0.55 a starving realm is poor, restive and losing its grip — yields under
 * half, the Great Families disgusted, garrisons thinning — and still climbing
 * back out the month somebody sells it grain, because `UNREST_DECAY` is
 * unopposed as soon as the bill is paid. Hunger makes a country ungovernable,
 * not empty.
 *
 * Famine never *lowers* unrest: a province already past this from conquest or a
 * chronicle answer keeps what it has.
 */
export const FAMINE_UNREST_CAP = 0.55;

/** Months of stores a crown wants in hand before it will bid on the book. */
export const FOOD_BID_MONTHS = 4;

/**
 * Months of stores a crown keeps before it will sell a grain, and it must stay
 * strictly above `FOOD_BID_MONTHS` — a crown that could be bidding and asking
 * for the same good at once would cross its own book. The same invariant
 * `AI_KEEP`/`AI_TARGET` keep for iron, stated here because these two are
 * computed rather than tabulated.
 */
export const FOOD_KEEP_MONTHS = 8;

/**
 * What a crown owes its people this month, in grain.
 *
 * Pure. The world map calls it, the market's council calls it, the estates call
 * it, and `doIncome` charges it — one formula, so a panel promising a bill of
 * nine cannot be followed by a month that takes eleven.
 */
export function foodBill(c: CampaignState, nation: number): number {
  let bill = 0;
  for (let p = 0; p < c.provinces.length; p++) {
    const prov = c.provinces[p];
    if (prov.owner !== nation) continue;
    bill += FOOD_PER_HOLDING + FOOD_PER_TIER * (tierOf(c, p) - 1);
  }
  for (const a of c.armies) {
    if (a.owner !== nation) continue;
    bill += rawPoints(a) * FOOD_PER_POINT;
  }
  return bill;
}

/** Grain in this crown's city yards. The barn half of the larder. */
export function yardFood(c: CampaignState, nation: number): number {
  let food = 0;
  for (const k of citiesOf(c, nation)) food += k.stock.grain;
  return food;
}

/** Everything a crown could eat this month: its yards and its national stores. */
export function foodStores(c: CampaignState, nation: number): number {
  return yardFood(c, nation) + (c.nations[nation]?.treasury.grain ?? 0);
}

/**
 * How many months the larder covers at the current bill. `Infinity` for a crown
 * with nothing to feed, which is the honest answer for a dead or landless one.
 */
export function monthsOfFood(c: CampaignState, nation: number): number {
  const bill = foodBill(c, nation);
  if (bill <= 0) return Number.POSITIVE_INFINITY;
  return foodStores(c, nation) / bill;
}

/**
 * Take the month's food out of the larder and return what could not be paid.
 *
 * Yards first, in province order — `c.cities` is kept sorted by province, so
 * which granary empties first is a fact about the board rather than about
 * iteration luck — and the national stores after.
 *
 * Writes nothing but stock and treasury; the consequence is `famine`'s job.
 */
export function drawFood(c: CampaignState, nation: number, amount: number): number {
  let owed = amount;
  if (owed <= 0) return 0;
  for (const k of citiesOf(c, nation)) {
    if (owed <= 0) break;
    const take = Math.min(owed, k.stock.grain);
    k.stock.grain -= take;
    owed -= take;
  }
  const treasury = c.nations[nation]?.treasury;
  if (treasury) {
    const take = Math.min(owed, treasury.grain);
    treasury.grain -= take;
    owed -= take;
  }
  // A rounding tail of a millionth of a grain is not a famine.
  return owed < 1e-6 ? 0 : owed;
}

/**
 * The month's meal, for one crown: charge the bill, and if it cannot be paid,
 * let the country notice.
 *
 * Called once per living crown from `doIncome`, after the tithe has been carted
 * to the yards — this month's harvest feeds this month — and before wages, so
 * the ledger reads collect, eat, pay.
 */
export function eatFood(c: CampaignState, nation: number): number {
  const bill = foodBill(c, nation);
  if (bill <= 0) return 0;
  const short = drawFood(c, nation, bill);
  if (short <= 0) return 0;

  /**
   * The one consequence, through the one door.
   *
   * Severity is the share of the bill that went unpaid, so a crown a tenth short
   * gets a tenth of the unrest — famine arrives as a slope rather than a cliff,
   * and a crown that is nearly fed stays nearly settled.
   */
  const severity = clamp(short / bill, 0, 1);
  for (let p = 0; p < c.provinces.length; p++) {
    const prov = c.provinces[p];
    if (prov.owner !== nation) continue;
    if (prov.unrest >= FAMINE_UNREST_CAP) continue;
    prov.unrest = clamp(prov.unrest + FAMINE_UNREST * severity, 0, FAMINE_UNREST_CAP);
  }
  if (nation === c.playerNation) {
    logLine(
      c,
      severity >= 0.999
        ? 'The granaries are empty. The realm goes hungry, and says so.'
        : `The granaries come up ${Math.ceil(short)} ${RESOURCE_LABELS.grain} short. There is bread rationing.`,
    );
  }
  return short;
}

/** Everything a panel needs to draw the larder without re-deriving the rule. */
export interface FoodReport {
  /** Grain owed this month. */
  bill: number;
  /** What the provinces will tithe next month, before unrest moves. */
  income: number;
  /** In the city yards. */
  yards: number;
  /** In the national treasury — the reserve, and what the market trades. */
  stores: number;
  /** Yards plus stores, over the bill. `Infinity` when there is nothing to feed. */
  months: number;
  /** What this month's bill would leave unpaid, given the larder as it stands. */
  shortfall: number;
  /** One line, already written. */
  sentence: string;
}

/**
 * What this crown's ground will tithe in grain next month, before unrest moves.
 *
 * The grain line of `projectedIncome`, taken off the same `holdingIncome` so the
 * larder panel and the income panel cannot disagree about a harvest.
 */
export function foodIncome(c: CampaignState, nation: number): number {
  let grain = 0;
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== nation) continue;
    for (const g of holdingIncome(c, p).goods) if (g.key === 'grain') grain += g.amount;
  }
  return grain;
}

/**
 * The larder, for the world map — the one reader, so a panel never re-derives
 * the rule and then disagrees with the month that charges it.
 *
 * Income is reported beside the bill rather than netted against it on purpose:
 * "you eat 9 and grow 11" is the sentence a player needs, and neither number
 * alone tells it.
 */
export function foodReport(c: CampaignState, nation: number): FoodReport {
  const bill = foodBill(c, nation);
  const income = foodIncome(c, nation);
  const yards = yardFood(c, nation);
  const stores = c.nations[nation]?.treasury.grain ?? 0;
  const total = yards + stores;
  const months = bill > 0 ? total / bill : Number.POSITIVE_INFINITY;
  const shortfall = Math.max(0, bill - total);
  return { bill, income, yards, stores, months, shortfall, sentence: foodSentence(bill, income, months, shortfall) };
}

function foodSentence(bill: number, income: number, months: number, shortfall: number): string {
  if (bill <= 0) return 'There is nobody left to feed.';
  if (shortfall > 0) {
    return `The realm eats ${bill.toFixed(1)} a month and is ${Math.ceil(shortfall)} short. Buy grain or the country will say so.`;
  }
  if (months < FOOD_BID_MONTHS) {
    return `${months.toFixed(1)} months of food in the granaries against a bill of ${bill.toFixed(1)}. One bad season from rationing.`;
  }
  if (income >= bill) {
    return `The realm eats ${bill.toFixed(1)} a month and grows ${income.toFixed(1)}. The granaries are filling.`;
  }
  return `${months.toFixed(1)} months of food in hand. The realm eats ${bill.toFixed(1)} a month and grows ${income.toFixed(1)}.`;
}
